// A/B test route map: legacy Next route pattern -> its redesign twin pattern.
//
// Keys/values are `router.pathname` patterns (the `/pages` file path, e.g. `/e/[recordId]`),
// NOT resolved URLs. A cohort user (auth group `redesignBeta`) on a legacy key is redirected to
// the mapped redesign twin by `useRedesignAbRedirect`. Dynamic segment names match 1:1 between
// each legacy route and its twin (`[recordId]` / `[id]`), so `router.replace({ pathname, query })`
// fills them and carries over any extra query params.
//
// Only legacy routes that have a redesign twin are listed. The legacy `/creators` list is absent
// (no twin); the creator profile and its platform sub-pages (`/creators/twitch/:id` etc.) are all
// mirrored. `/design-preview` is a throwaway preview, not an A/B target.
//
// NOTE: per `docs/redesign.md`, some twins still defer functionality to their legacy page (Bulk CSV
// on add-campaign + keys-and-products, In-Game rewards, my-keys "Keys for Giveaways", drop-admin
// audit approve/reject, campaigns one-click publish). Cohort users lose those on the redesign page;
// drop the relevant entry below to keep a page on legacy for everyone.
export const REDESIGN_AB_ROUTE_MAP = {
    '/drops': '/nova/drops',
    '/e/[recordId]': '/nova/drop/[recordId]',
    '/dare/[recordId]': '/nova/dare/[recordId]',
    '/creators/[recordId]': '/nova/creators/[recordId]',
    // Platform-specific profile URLs are mirrored 1:1 (structure preserved): each has its own nova
    // page rendering the same redesign profile, so `/creators/twitch/x` -> `/nova/creators/twitch/x`.
    '/creators/twitch/[recordId]': '/nova/creators/twitch/[recordId]',
    '/creators/yt/[recordId]': '/nova/creators/yt/[recordId]',
    '/creators/tiktok/[recordId]': '/nova/creators/tiktok/[recordId]',
    '/add-campaign': '/nova/add-campaign',
    '/campaign-dashboard': '/nova/campaign-dashboard',
    '/campaign-dashboard/[recordId]': '/nova/campaign-dashboard/[recordId]',
    '/campaign-clips': '/nova/campaign-clips',
    '/campaign-clips/[recordId]': '/nova/campaign-clips/[recordId]',
    '/applications': '/nova/applications',
    '/application/[id]': '/nova/application/[id]',
    '/brand-users': '/nova/brand-users',
    '/publisher-dares': '/nova/publisher-dares',
    '/whitelisted-creators': '/nova/whitelisted-creators',
    '/publisher-banned-creators': '/nova/banned-creators',
    '/keys-and-products': '/nova/keys-and-products',
    '/my-keys': '/nova/my-keys',
    '/drop-admin': '/nova/drop-admin',
    '/creator-dashboard': '/nova/creator-dashboard',
    '/creators-leaderboard': '/nova/creators-leaderboard',
    '/review-clips': '/nova/review-clips',
    '/audience-targeting': '/nova/audience-targeting',
    '/review-project/[recordId]': '/nova/review-project/[recordId]',
    '/review-drop-project/[recordId]': '/nova/review-drop-project/[recordId]',
    '/pending-videos': '/nova/pending-videos',
    '/brand-approve-dares': '/nova/brand-approve-dares',
    '/brands-reports': '/nova/brands-reports',
    '/campaigns': '/nova/campaigns',
    '/privacy-policy': '/nova/privacy-policy',
    '/cookie-policy': '/nova/cookie-policy',
    '/terms': '/nova/terms',
    '/FAQ': '/nova/FAQ',
    // Account verification flow. NOTE: a fresh signup is signed out, so the `groups` cookie cannot
    // carry `redesignBeta` and the server-side `_app` redirect never fires here; cohort entry to these
    // twins is the client-side gate (localStorage override, or an already-grouped user re-verifying).
    '/verify-account': '/nova/verify-account',
    '/verify-account-finished': '/nova/verify-account-finished',
    '/verify-account-failure': '/nova/verify-account-failure',
    '/account-verified': '/nova/account-verified',
};
// Returns the redesign twin pattern for a legacy `router.pathname`, or undefined if the route
// has no twin (so the user stays on the legacy page).
export const getRedesignTwinPathname = (pathname) => REDESIGN_AB_ROUTE_MAP[pathname];
// Reverse of REDESIGN_AB_ROUTE_MAP: redesign twin pattern -> its legacy pattern. Used by the client
// A/B gate to send NON-cohort users out of a `/nova/*` route back to the legacy page (the mirror of
// the forward legacy -> redesign redirect). Redesign-only `/nova` pages absent from the map have no
// legacy twin, so the gate falls back to home for those.
const REDESIGN_AB_ROUTE_MAP_REVERSED = Object.entries(REDESIGN_AB_ROUTE_MAP)
    .reduce((acc, [legacyPathname, redesignPathname]) => {
    acc[redesignPathname] = legacyPathname;
    return acc;
}, {});
// Returns the legacy pattern for a redesign `/nova/*` `router.pathname`, or undefined if that
// redesign page has no legacy twin.
export const getLegacyTwinPathname = (pathname) => REDESIGN_AB_ROUTE_MAP_REVERSED[pathname];
// Resolve the twin to a concrete URL string for a legacy route pattern + its params/query.
// Used server-side (the `_app` getInitialProps redirect) where Next's object form
// `router.replace({ pathname, query })` is not available: substitutes the twin's `[param]` /
// `[...param]` segments from `query` and re-appends any leftover query params. Returns undefined
// when the route has no twin.
export const getRedesignTwinUrl = (pathname, query = {}) => {
    const twin = REDESIGN_AB_ROUTE_MAP[pathname];
    if (!twin) {
        return undefined;
    }
    const usedKeys = new Set();
    const path = twin.replace(/\[(?:\.\.\.)?([^\]]+)\]/g, (_match, name) => {
        usedKeys.add(name);
        const value = query[name];
        return Array.isArray(value) ? value.join('/') : (value !== null && value !== void 0 ? value : '');
    });
    const params = new URLSearchParams();
    Object.keys(query).forEach((key) => {
        if (usedKeys.has(key)) {
            return;
        }
        const value = query[key];
        if (Array.isArray(value)) {
            value.forEach(item => params.append(key, item));
        }
        else if (value !== undefined) {
            params.append(key, value);
        }
    });
    const queryString = params.toString();
    return queryString ? `${path}?${queryString}` : path;
};
